/**
 * Self-identity resolution for the DIPENDENTE experience.
 *
 * Authoritative source: `me.employee_legacy_id` from /api/me (server-side lookup
 * `employees WHERE auth_user_id = current_user.id`). This ID is the ONLY signal
 * we trust to identify "self". We do NOT use:
 *   - `dipendentiInScope[0]` (positional / fallback)
 *   - any "first-of-scope" heuristic
 *
 * DEV impersonation returns a clearly-flagged demo persona so the operator
 * knows they are looking at simulated data, never real data of another employee.
 */
import { useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useApp } from '@/context/AppContext';

export function useSelfEmployee() {
  const { me } = useAuth();
  const { dipendenti, devImpersonateRoleId, devImpersonatePersonaId, currentRoleId } = useApp();
  return useMemo(() => {
    // DEV → specific real persona picked from the "Simula persona" submenu.
    if (currentRoleId === 'ceo' && devImpersonatePersonaId) {
      const target = dipendenti.find(d => d.id === devImpersonatePersonaId) || null;
      return { self: target, isSimulated: true, notLinked: !target, source: 'dev-real-persona' };
    }
    // DEV → generic "Dipendente" role has no implicit real employee.
    // Personal APIs must never be queried for a different employee using the
    // client-only simulation state.
    if (currentRoleId === 'ceo' && devImpersonateRoleId === 'dipendente') {
      return { self: null, isSimulated: true, notLinked: true, source: 'dev-generic' };
    }
    // Real employee login → resolve by auth_user_id via server-side legacy_id.
    const legacy = me?.employee_legacy_id || null;
    if (!legacy) {
      return { self: null, isSimulated: false, notLinked: true, source: 'auth' };
    }
    const bound = dipendenti.find(d => d.id === legacy) || null;
    return { self: bound, isSimulated: false, notLinked: !bound, source: 'auth' };
  }, [me?.employee_legacy_id, dipendenti, devImpersonateRoleId, devImpersonatePersonaId, currentRoleId]);
}
